import { useCallback, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import {
  Badge,
  Button,
  CounterBadge,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  Switch,
  Tab,
  TabList,
  Text,
  makeStyles,
  tokens,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from '@fluentui/react-components';
import {
  AppsAddIn20Regular,
  ArrowSync20Regular,
  ArrowUndo20Regular,
  BotSparkle20Regular,
  BrainCircuit20Regular,
  Delete20Regular,
  Edit20Regular,
  MoreHorizontal20Regular,
  PlugConnected20Regular,
  Save20Regular,
} from '@fluentui/react-icons';
import { JsonEditor } from '@/components/WorkflowDesigner/JsonEditor';
import { ConnectorName } from './ConnectorName';
import { DeleteConnectionDialog } from './EditDialogs/DeleteConnectionDialog';
import { EditAgentConnectionDialog } from './EditDialogs/EditAgentConnectionDialog';
import { EditConnectionDialog } from './EditDialogs/EditConnectionDialog';
import { EditMcpConnectionDialog } from './EditDialogs/EditMcpConnectionDialog';
import { EditServiceProviderConnectionDialog } from './EditDialogs/EditServiceProviderConnectionDialog';
import {
  ConnectionActionsMenu,
  type JsonConnectionActionTarget,
} from './ConnectionActionsMenu';
import {
  ConnectionTypeSection,
  type ConnectionTypeColumn,
} from './ConnectionTypeSection';
import type { ConnectionsEditorState, ConnectionsTabValue } from './useConnectionsEditor';
import type { AvailableConnection } from '@/types';
import { ConnectionStatus } from '@/types';
import type {
  AgentConnection,
  McpConnection,
} from '@/types/logicApps';
import type { ServiceProviderConnection } from '@/types/connections';
import { extractConnectorName } from '@/utils/connections';
import { getAdoConnectorApiName, getGitHubConnectorApiName } from '@/services/repoConnectionService';
import { isAgentConnectionUsable, isServiceProviderConnectionUsable } from '@/services/connectionService';

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Keep horizontal overflow visible so tabList's negative margin doesn't
    // clip the first tab's focus indicator; content clips the tab body.
    overflowY: 'hidden',
    height: '100%',
  },
  tabList: {
    flexShrink: 0,
    // Fluent's <Overflow> measures the TabList (its single child) to decide
    // which items overflow into the menu. Inside the new flex row, the
    // TabList must claim available width — otherwise Overflow sees an
    // intrinsic-width container and hides almost every tab.
    flex: 1,
    minWidth: 0,
    // Fluent <Tab> ships with intrinsic horizontal padding of
    // `spacingHorizontalMNudge` (medium size). Pulling the TabList left by
    // the same token lands the first tab's content flush with the section
    // <h2> above, matching the other app-level pages.
    marginLeft: `calc(-1 * ${tokens.spacingHorizontalMNudge})`,
    // Vertical breathing room so the focus indicator on the top edge
    // isn't clipped by the parent's `overflow: hidden`.
    paddingTop: tokens.spacingVerticalXXS,
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
  },
  tabBadge: {
    // Tabular numerals keep tab widths from jiggling when counts change.
    fontVariantNumeric: 'tabular-nums',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  connectionName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  connectorName: {
    color: tokens.colorNeutralForeground2,
  },
  monoCell: {
    color: tokens.colorNeutralForeground2,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    wordBreak: 'break-all',
  },
  actionsCell: {
    width: '48px',
  },
  jsonEditor: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  tabsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
    flexShrink: 0,
  },
  viewHeaderSwitch: {
    // Fixed width label avoids layout shift when toggling Draft/Production
    '& .fui-Switch__label': {
      minWidth: '70px',
    },
  },
  jsonError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalS,
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground2,
  },
  footerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
});

export interface ConnectionsViewProps {
  state: ConnectionsEditorState;
}

// Repo-OAuth connectors used internally by sandbox repository settings — hide from user-facing list.
// Includes both the env-configured names and the managed-connector alternatives.
const HIDDEN_CONNECTORS = new Set([
  getGitHubConnectorApiName().toLowerCase(),
  getAdoConnectorApiName().toLowerCase(),
  'azurelogicappsgithub',
  'azurelogicappsado',
]);

type OverflowTabDescriptor = { value: string; label: string; count: number | null };

const OverflowTabMenuItem = ({
  tab,
  onSelect,
}: {
  tab: OverflowTabDescriptor;
  onSelect: (value: string) => void;
}) => {
  const isVisible = useIsOverflowItemVisible(tab.value);
  if (isVisible) return null;
  return (
    <MenuItem onClick={() => onSelect(tab.value)}>
      {tab.label}
      {typeof tab.count === 'number' ? ` (${tab.count})` : ''}
    </MenuItem>
  );
};

/**
 * Renders a "More" menu button that appears only when one or more tabs do
 * not fit on screen. Hidden tabs are listed inside the menu so they remain
 * reachable by mouse and keyboard on narrow viewports — without it the
 * TabList would clip and a11y breaks (no way to focus the hidden tabs).
 */
const OverflowTabsMenu = ({
  tabs,
  selectedValue,
  onSelect,
}: {
  tabs: OverflowTabDescriptor[];
  selectedValue: string;
  onSelect: (value: string) => void;
}) => {
  const { ref, overflowCount, isOverflowing } = useOverflowMenu<HTMLButtonElement>();
  if (!isOverflowing) return null;
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          // MenuTrigger's cloneElement narrows the child ref to `Ref<never>`,
          // but `useOverflowMenu` hands us a real HTMLButtonElement ref. Cast
          // to bridge the two — the runtime types are correct.
          ref={ref as unknown as Ref<never>}
          appearance="transparent"
          icon={<MoreHorizontal20Regular />}
          aria-label={`${overflowCount} more tab${overflowCount === 1 ? '' : 's'}`}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {tabs.map((tab) => (
            <OverflowTabMenuItem
              key={tab.value}
              tab={tab}
              onSelect={(v) => {
                if (v !== selectedValue) onSelect(v);
              }}
            />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

/**
 * Tab list + tab content for the Connections editor. Does not render any chrome
 * (header/footer) — that's the wrapping dialog or page's responsibility.
 */
export const ConnectionsView = ({ state }: ConnectionsViewProps) => {
  const styles = useStyles();
  const {
    selectedTab,
    setSelectedTab,
    userCanEdit,
    permissionsLoading,
    connections,
    isLoading,
    isError,
    refetch,
    getConnectorId,
    handleDeleteConnection,
    handleSyncConnection,
    agentConnections,
    serviceProviderConnections,
    mcpConnections,
    isJsonLoading,
    isJsonError,
    refetchConnectionsJson,
    rawJson,
    jsonError,
    isValidJson,
    handleJsonChange,
    isDraftMode,
    setIsDraftMode,
    isDraftLoading,
    isDraftError,
    refetchDraftJson,
    handleDeleteLocalConnection,
    handleSaveLocalConnection,
  } = state;

  const dataLoading = useMemo(() => isLoading || isJsonLoading || isDraftLoading, [isLoading, isJsonLoading, isDraftLoading]);
  const filteredConnections = useMemo(
    () =>
      connections?.filter((c) => {
        const name = extractConnectorName(c.connectorId ?? '').toLowerCase();
        return !HIDDEN_CONNECTORS.has(name);
      }),
    [connections],
  );

  // Pending-delete state powers the confirmation dialog. Deleting a connection
  // permanently removes the underlying ARM resource — workflows that reference
  // it will fail at runtime — so we always confirm before firing the request.
  const [pendingDelete, setPendingDelete] = useState<AvailableConnection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Keep the last non-null value around so the dialog body keeps showing the
  // connection name during Fluent's close transition. Without this, the
  // <strong>{name}</strong> text flickers to empty for the duration of the
  // close animation when the user clicks Cancel.
  const lastPendingDelete = useRef<AvailableConnection | null>(null);
  if (pendingDelete && pendingDelete !== lastPendingDelete.current) {
    lastPendingDelete.current = pendingDelete;
  }
  const displayedDelete = pendingDelete ?? lastPendingDelete.current;

  // Edit connection state — opens the EditConnectionDialog
  const [pendingEdit, setPendingEdit] = useState<AvailableConnection | null>(null);

  // JSON-backed connection (SP / agent / MCP) edit + delete state. These live
  // in connections.json (or connections-draft.json in draft mode) rather than
  // as ARM resources, so the edit/delete handlers come straight from the
  // editor hook and write to the active connections file.
  const [pendingJsonEdit, setPendingJsonEdit] = useState<JsonConnectionActionTarget | null>(null);
  const [pendingJsonDelete, setPendingJsonDelete] = useState<JsonConnectionActionTarget | null>(null);
  const [isJsonDeleting, setIsJsonDeleting] = useState(false);
  const lastPendingJsonDelete = useRef<JsonConnectionActionTarget | null>(null);
  if (pendingJsonDelete && pendingJsonDelete !== lastPendingJsonDelete.current) {
    lastPendingJsonDelete.current = pendingJsonDelete;
  }
  const displayedJsonDelete = pendingJsonDelete ?? lastPendingJsonDelete.current;

  // Stable identity callbacks so the ConnectionActionsMenu instances
  // don't force the column-render memos to recompute every render.
  const handleJsonEditClick = useCallback(
    (target: JsonConnectionActionTarget) => setPendingJsonEdit(target),
    [],
  );
  const handleJsonDeleteClick = useCallback(
    (target: JsonConnectionActionTarget) => setPendingJsonDelete(target),
    [],
  );

  const getActionsMenu = useCallback((kind: JsonConnectionActionTarget['kind']) => {
    return userCanEdit && !dataLoading
        ? [
            {
              header: '',
              cellClassName: styles.actionsCell,
              headerClassName: styles.actionsCell,
              render: (id: string, conn: ServiceProviderConnection | AgentConnection | McpConnection) =>
                <ConnectionActionsMenu
                  target={{ kind, id, displayName: conn.displayName ?? id }}
                  onEdit={handleJsonEditClick}
                  onDelete={handleJsonDeleteClick}
                />
            },
          ]
        : [];
  }, [dataLoading, handleJsonDeleteClick, handleJsonEditClick, styles.actionsCell, userCanEdit]);

  const pendingJsonEditValue = useMemo(() => {
    if (!pendingJsonEdit) return null;
    switch (pendingJsonEdit.kind) {
      case 'serviceProvider':
        return serviceProviderConnections?.[pendingJsonEdit.id] ?? null;
      case 'agent':
        return agentConnections?.[pendingJsonEdit.id] ?? null;
      case 'mcp':
        return mcpConnections?.[pendingJsonEdit.id] ?? null;
    }
  }, [pendingJsonEdit, serviceProviderConnections, agentConnections, mcpConnections]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await handleDeleteConnection(pendingDelete);
      await refetchConnectionsJson();
      await refetchDraftJson();
      setPendingDelete(null);
    } catch {
      // handleDeleteConnection already toasts on failure and re-throws so
      // upstream callers can react. Swallow here intentionally — re-throwing
      // out of a React event handler surfaces as an unhandled rejection.
      // Leaving pendingDelete set keeps the dialog open so the user can retry.
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmJsonDelete = async () => {
    if (!pendingJsonDelete) return;
    setIsJsonDeleting(true);
    try {
      await handleDeleteLocalConnection(pendingJsonDelete.id, pendingJsonDelete.kind);
      setPendingJsonDelete(null);
    } catch {
      // Hook already toasted; keep dialog open for retry.
    } finally {
      setIsJsonDeleting(false);
    }
  };

  // ── Column definitions per connection type ────────────────────────────────

  const managedColumns: Array<ConnectionTypeColumn<AvailableConnection>> = useMemo(
    () => [
      {
        header: 'Name',
        render: (_id, connection) => (
          <Text className={styles.connectionName}>
            {connection.displayName ?? connection.name}
          </Text>
        ),
      },
      {
        header: 'Connector',
        render: (_id, connection) => (
          <Text className={styles.connectorName}>
            <ConnectorName apiId={getConnectorId(connection)} />
          </Text>
        ),
      },
      {
        header: 'Status',
        render: (_id, connection) => {
          const isConnected = connection.status === ConnectionStatus.Connected;
          return (
            <Badge
              appearance="filled"
              color={isConnected ? 'success' : 'danger'}
            >
              {isConnected ? 'Connected' : 'Not connected'}
            </Badge>
          );
        },
      },
      ...(userCanEdit && !dataLoading
        ? [
            {
              header: '',
              cellClassName: styles.actionsCell,
              headerClassName: styles.actionsCell,
              render: (_id: string, connection: AvailableConnection) => (
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      appearance="subtle"
                      icon={<MoreHorizontal20Regular />}
                      size="medium"
                      aria-label={`More actions for ${connection.displayName ?? connection.name}`}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem
                        icon={<ArrowSync20Regular />}
                        onClick={() => handleSyncConnection(connection)}
                      >
                        Sync
                      </MenuItem>
                      <MenuItem
                        icon={<Edit20Regular />}
                        onClick={() => setPendingEdit(connection)}
                      >
                        Edit
                      </MenuItem>
                      <MenuDivider />
                      <MenuItem
                        icon={<Delete20Regular />}
                        onClick={() => setPendingDelete(connection)}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              ),
            },
          ]
        : []),
    ],
    [userCanEdit, dataLoading, styles.actionsCell, styles.connectionName, styles.connectorName, getConnectorId, handleSyncConnection],
  );

  const serviceProviderColumns: Array<ConnectionTypeColumn<ServiceProviderConnection>> = useMemo(
    () => [
      {
        header: 'Name',
        render: (id, conn) => (
          <Text className={styles.connectionName}>{conn.displayName ?? id}</Text>
        ),
      },
      {
        header: 'Service provider',
        render: (_id, conn) => (
          <Text className={styles.connectorName}>
            <ConnectorName apiId={conn.serviceProvider?.id} />
          </Text>
        ),
      },
      ...(getActionsMenu('serviceProvider')),
    ],
    [getActionsMenu, styles.connectionName, styles.connectorName],
  );

  const agentColumns: Array<ConnectionTypeColumn<AgentConnection>> = useMemo(
    () => [
      {
        header: 'Name',
        render: (id, conn) => (
          <Text className={styles.connectionName}>{conn.displayName ?? id}</Text>
        ),
      },
      {
        header: 'Deployment / model',
        render: (_id, conn) => (
          <Text className={styles.connectorName}>
            {conn.modelConfiguration?.deploymentName ?? conn.modelConfiguration?.modelName ?? '—'}
          </Text>
        ),
      },
      ...(getActionsMenu('agent')),
    ],
    [getActionsMenu, styles.connectionName, styles.connectorName],
  );

  const mcpColumns: Array<ConnectionTypeColumn<McpConnection>> = useMemo(
    () => [
      {
        header: 'Name',
        render: (id, conn) => (
          <Text className={styles.connectionName}>{conn.displayName ?? id}</Text>
        ),
      },
      {
        header: 'Endpoint',
        cellClassName: styles.monoCell,
        render: (_id, conn) => <span>{conn.mcpServerUrl || '—'}</span>,
      },
      ...(getActionsMenu('mcp')),
    ],
    [getActionsMenu, styles.connectionName, styles.monoCell],
  );

  // ── Section configurations ────────────────────────────────────────────────

  const managedEntries = useMemo(
    () => (filteredConnections ?? []).map((c) => [c.id, c] as [string, AvailableConnection]),
    [filteredConnections],
  );

  // Filter out template-apply placeholder rows: a freshly-applied template
  // writes a service-provider / agent entry that has only `displayName` +
  // discriminator (`serviceProvider.id` / `modelProvider.id`), so the canvas
  // badge can render as "configuration incomplete" instead of "reference not
  // found". Those placeholders are not real connections and hiding them from
  // the Connections list prevents the confusing non-actionable rows.
  const serviceProviderEntries = useMemo(
    () => Object.entries(serviceProviderConnections ?? {}).filter(
      ([, conn]) => isServiceProviderConnectionUsable(conn),
    ),
    [serviceProviderConnections],
  );

  const agentEntries = useMemo(
    () => Object.entries(agentConnections ?? {}).filter(
      ([, conn]) => isAgentConnectionUsable(conn),
    ),
    [agentConnections],
  );

  const mcpEntries = useMemo(() => Object.entries(mcpConnections ?? {}), [mcpConnections]);

  // ── Tab descriptors ───────────────────────────────────────────────────────

  type TabDescriptor = {
    value: ConnectionsTabValue;
    label: string;
    count: number | null;
    render: () => ReactNode;
  };

  const tabs: TabDescriptor[] = [
    {
      value: 'managed',
      label: 'Managed connections',
      count: managedEntries.length,
      render: () => (
        <ConnectionTypeSection<AvailableConnection>
          entries={managedEntries}
          columns={managedColumns}
          isLoading={isLoading}
          isError={isError}
          onRetry={refetch}
          emptyIcon={<PlugConnected20Regular aria-hidden="true" />}
          emptyMessage="No managed connections found for this app."
          loadingLabel="Loading connections..."
          errorMessage="Failed to load connections."
          ariaLabel="Connections"
        />
      ),
    },
    {
      value: 'serviceProvider',
      label: 'Service provider connections',
      count: serviceProviderEntries.length,
      render: () => (
        <ConnectionTypeSection<ServiceProviderConnection>
          entries={serviceProviderEntries}
          columns={serviceProviderColumns}
          isLoading={isJsonLoading}
          isError={isJsonError}
          onRetry={refetchConnectionsJson}
          emptyIcon={<AppsAddIn20Regular aria-hidden="true" />}
          emptyMessage="No service provider connections found for this app."
          errorMessage="Failed to load service provider connections."
          ariaLabel="Service provider connections"
        />
      ),
    },
    {
      value: 'agent',
      label: 'Agent connections',
      count: agentEntries.length,
      render: () => (
        <ConnectionTypeSection<AgentConnection>
          entries={agentEntries}
          columns={agentColumns}
          isLoading={isJsonLoading}
          isError={isJsonError}
          onRetry={refetchConnectionsJson}
          emptyIcon={<BrainCircuit20Regular aria-hidden="true" />}
          emptyMessage="No agent connections found for this app."
          errorMessage="Failed to load agent connections."
          ariaLabel="Agent connections"
        />
      ),
    },
    {
      value: 'mcp',
      label: 'MCP connections',
      count: mcpEntries.length,
      render: () => (
        <ConnectionTypeSection<McpConnection>
          entries={mcpEntries}
          columns={mcpColumns}
          isLoading={isJsonLoading}
          isError={isJsonError}
          onRetry={refetchConnectionsJson}
          emptyIcon={<BotSparkle20Regular aria-hidden="true" />}
          emptyMessage="No MCP connections found for this app."
          errorMessage="Failed to load MCP connections."
          ariaLabel="MCP connections"
        />
      ),
    },
    {
      value: 'raw',
      label: 'JSON',
      count: null,
      render: () => renderRawTab(),
    },
  ];

  const renderRawTab = () => {
    const activeLoading = isDraftMode ? isDraftLoading : isJsonLoading;
    const activeError = isDraftMode ? isDraftError : isJsonError;
    const activeRefetch = isDraftMode ? refetchDraftJson : refetchConnectionsJson;
    const fileName = isDraftMode ? 'connections-draft.json' : 'connections.json';

    if (activeLoading) {
      return (
        <div className={styles.status} role="status" aria-live="polite">
          <Text>Loading {fileName}...</Text>
        </div>
      );
    }
    if (activeError) {
      return (
        <div className={styles.status} role="alert">
          <Text>
            Failed to load {fileName}.{' '}
            <Button appearance="transparent" onClick={() => activeRefetch()}>
              Retry
            </Button>
          </Text>
        </div>
      );
    }
    return (
      <div className={styles.jsonEditor}>
        <JsonEditor
          value={rawJson}
          onChange={handleJsonChange}
          fill
          hasError={!isValidJson}
          readOnly={permissionsLoading || !userCanEdit}
        />
        {jsonError && <Text className={styles.jsonError}>{jsonError}</Text>}
      </div>
    );
  };

  const activeTab = tabs.find((t) => t.value === selectedTab);

  // ── Delete confirmation dialog props ──────────────────────────────────────
  //
  // Both the managed-connection menu and the JSON-backed actions menu open
  // the same `DeleteConnectionDialog`; only the copy + test id + which
  // mutation runs differ. Only one pending-delete slot can be non-null at a
  // time (they're set from disjoint UI paths). During Fluent's close
  // animation `pendingXxxDelete` flips to null while the dialog is still
  // visually mounted, so `lastDeleteKindRef` keeps rendering the right
  // copy for the flow that just closed \u2014 same shape as the
  // `lastPendingDelete` / `lastPendingJsonDelete` refs above.
  const lastDeleteKindRef = useRef<'managed' | 'json'>('managed');
  if (pendingDelete) lastDeleteKindRef.current = 'managed';
  else if (pendingJsonDelete) lastDeleteKindRef.current = 'json';
  const activeDeleteKind: 'managed' | 'json' =
    pendingDelete ? 'managed' : pendingJsonDelete ? 'json' : lastDeleteKindRef.current;

  const deleteDialogProps =
    activeDeleteKind === 'managed'
      ? {
          open: pendingDelete !== null,
          onOpenChange: (next: boolean) => {
            if (!next) setPendingDelete(null);
          },
          isDeleting,
          onConfirm: confirmDelete,
          testId: 'delete-connection-dialog',
          description: (
            <>
              This will permanently delete the connection resource{' '}
              <strong>{displayedDelete?.displayName ?? displayedDelete?.name}</strong>.
              Any workflow that uses this connection will fail until it is reconnected.
            </>
          ),
        }
      : {
          open: pendingJsonDelete !== null,
          onOpenChange: (next: boolean) => {
            if (!next) setPendingJsonDelete(null);
          },
          isDeleting: isJsonDeleting,
          onConfirm: confirmJsonDelete,
          testId: 'delete-json-connection-dialog',
          description: (
            <>
              This will remove{' '}
              <strong>{displayedJsonDelete?.displayName ?? '—'}</strong>{' '}
              from{' '}
              {isDraftMode ? 'connections draft' : 'connections'}.
              Any workflow that references this connection will fail until it is
              reconnected.
            </>
          ),
        };

  return (
    <div className={styles.root}>
      <div className={styles.tabsRow}>
        <Overflow minimumVisible={1}>
          <TabList
            className={styles.tabList}
            aria-label="Connections"
            selectedValue={selectedTab}
            onTabSelect={(_, data) => setSelectedTab(data.value as ConnectionsTabValue)}
          >
            {tabs.map((tab) => (
              <OverflowItem key={tab.value} id={tab.value} priority={0}>
                {/* The CounterBadge is aria-hidden by design: tab count
                    affordances are visual only — screen readers would otherwise
                    hear "Managed connections 3" on every focus, which is noise
                    rather than signal. The Tab keeps the bare label as its
                    accessible name. */}
                <Tab value={tab.value} aria-label={tab.label}>
                  <span className={styles.tabLabel}>
                    {tab.label}
                    {typeof tab.count === 'number' && (
                      <CounterBadge
                        className={styles.tabBadge}
                        count={tab.count}
                        showZero
                        appearance={selectedTab === tab.value ? 'filled' : 'ghost'}
                        color={selectedTab === tab.value ? 'brand' : 'informative'}
                        size="small"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </Tab>
              </OverflowItem>
            ))}
            <OverflowTabsMenu
              tabs={tabs.map((t) => ({ value: t.value, label: t.label, count: t.count }))}
              selectedValue={selectedTab}
              onSelect={(v) => setSelectedTab(v as ConnectionsTabValue)}
            />
          </TabList>
        </Overflow>
        <Switch
          className={styles.viewHeaderSwitch}
          checked={isDraftMode}
          onChange={(_, data) => setIsDraftMode(data.checked)}
          label={isDraftMode ? 'Draft mode' : 'Production'}
          labelPosition="before"
        />
      </div>
      <div className={styles.content}>{activeTab?.render()}</div>

      <DeleteConnectionDialog {...deleteDialogProps} />

      {pendingEdit !== null ? <EditConnectionDialog
        open={pendingEdit !== null}
        onClose={() => setPendingEdit(null)}
        connection={pendingEdit}
        onSaved={refetch}
      /> : null}

      {pendingJsonEdit && pendingJsonEdit.kind === 'mcp' && pendingJsonEditValue && (
        <EditMcpConnectionDialog
          open
          onClose={() => setPendingJsonEdit(null)}
          connectionKey={pendingJsonEdit.id}
          connection={pendingJsonEditValue as McpConnection}
          onSave={async (recordKey, record) => {
            // The dialog closes itself via `onAdded` after a successful save;
            // routing goes through `handleSaveLocalConnection` so draft/prod
            // mode is honored the same way as SP/agent edits.
            await handleSaveLocalConnection(recordKey, 'mcp', record);
          }}
        />
      )}

      {pendingJsonEdit && pendingJsonEdit.kind === 'agent' && pendingJsonEditValue && (
        <EditAgentConnectionDialog
          open
          onClose={() => setPendingJsonEdit(null)}
          connectionKey={pendingJsonEdit.id}
          connection={pendingJsonEditValue as AgentConnection}
          onSave={async (recordKey, record) => {
            // Same draft/prod routing as MCP — the hook picks the right file.
            await handleSaveLocalConnection(recordKey, 'agent', record);
          }}
        />
      )}

      {pendingJsonEdit && pendingJsonEdit.kind === 'serviceProvider' && pendingJsonEditValue && (
        <EditServiceProviderConnectionDialog
          open
          onClose={() => setPendingJsonEdit(null)}
          connectionKey={pendingJsonEdit.id}
          connection={pendingJsonEditValue as ServiceProviderConnection}
          onSave={async (recordKey, record) => {
            // Same draft/prod routing as MCP + agent — the hook picks the
            // right file. `saveJsonConnection` is bypassed because the SP
            // dialog does its own app-settings extraction before persist.
            await handleSaveLocalConnection(recordKey, 'serviceProvider', record);
          }}
        />
      )}
    </div>
  );
};

ConnectionsView.displayName = 'ConnectionsView';

export interface ConnectionsFooterActionsProps {
  state: ConnectionsEditorState;
}

/**
 * Save/Discard actions for the Connections editor. Only appears on the JSON
 * tab — the other tabs are read-only listings, so showing perpetually-disabled
 * buttons created an unnecessary affordance and visual noise.
 */
export const ConnectionsFooterActions = ({ state }: ConnectionsFooterActionsProps) => {
  const styles = useStyles();
  const { selectedTab, userCanEdit, permissionsLoading, hasChanges, isValidJson, isSaving, handleSave, handleDiscard } = state;

  if (selectedTab !== 'raw') return null;

  const cantEditYet = !userCanEdit || permissionsLoading;

  return (
    <div className={styles.footerActions}>
      <Button
        appearance="secondary"
        icon={<ArrowUndo20Regular />}
        onClick={handleDiscard}
        disabled={cantEditYet || !hasChanges || isSaving}
      >
        Discard
      </Button>
      <Button
        appearance="primary"
        icon={<Save20Regular />}
        onClick={handleSave}
        disabled={cantEditYet || !hasChanges || !isValidJson || isSaving}
      >
        {isSaving ? 'Saving\u2026' : 'Save'}
      </Button>
    </div>
  );
};

ConnectionsFooterActions.displayName = 'ConnectionsFooterActions';
